import React, { useEffect, useRef } from "react";
import shaka from "shaka-player/dist/shaka-player.ui";
import "shaka-player/dist/controls.css";
import type { CardVideoMiniPlayerMeta } from "@/cards/common/component/background-players/cardVideoMiniPlayerStore";
import {
  playExclusiveCardMedia,
  promoteCardShakaToMiniPlayer,
  registerCardVideoElement,
  releaseCardVideoElement,
} from "@/cards/common/component/background-players/singleCardMedia";

type ShakaPlayerVideoProps = {
  src: string;
  autoPlay?: boolean;
  /** Seek here after load (used when resuming in the mini player). */
  startTime?: number;
  /**
   * When true, register exclusive playback and hand off to the floating
   * mini player if this component unmounts while playing.
   * Keep false inside CardVideoMiniPlayer itself.
   */
  enableMiniPlayer?: boolean;
  miniPlayerMeta?: CardVideoMiniPlayerMeta;
  onPlayStateChange?: (playing: boolean) => void;
};

type ShakaPlayerInstance = InstanceType<typeof shaka.Player>;
type ShakaOverlay = InstanceType<typeof shaka.ui.Overlay>;

const ShakaPlayerVideo = ({
  src,
  autoPlay = false,
  startTime = 0,
  enableMiniPlayer = false,
  miniPlayerMeta,
  onPlayStateChange,
}: ShakaPlayerVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<ShakaPlayerInstance | null>(null);
  const uiRef = useRef<ShakaOverlay | null>(null);
  const metaRef = useRef(miniPlayerMeta);
  metaRef.current = miniPlayerMeta;
  const wasPlayingRef = useRef(false);
  const currentTimeRef = useRef(0);
  const handedOffRef = useRef(false);
  const startTimeRef = useRef(startTime);
  const autoPlayRef = useRef(autoPlay);
  startTimeRef.current = startTime;
  autoPlayRef.current = autoPlay;

  // Player lifecycle (create / destroy). Declared first so its cleanup runs LAST.
  // Reload only when src / enableMiniPlayer change — startTime/autoPlay are read from refs.
  useEffect(() => {
    let player: ShakaPlayerInstance | undefined;
    let ui: ShakaOverlay | undefined;
    let cancelled = false;
    handedOffRef.current = false;
    // Freeze resume values for this load so store ticks cannot remount the player.
    const resumeAtOnLoad = Math.max(0, Number(startTimeRef.current) || 0);
    const shouldAutoPlay = Boolean(autoPlayRef.current);

    const initPlayer = async () => {
      shaka.polyfill.installAll();

      if (!shaka.Player.isBrowserSupported()) {
        console.error("Shaka Player is not supported");
        return;
      }

      const video = videoRef.current;
      const container = containerRef.current;

      if (!video || !container) {
        return;
      }

      player = new shaka.Player(video);
      playerRef.current = player;

      player.addEventListener("error", (event: Event) => {
        const errorEvent = event as Event & { detail?: unknown };
        console.error("Shaka error:", errorEvent.detail);
      });

      ui = new shaka.ui.Overlay(player, container, video);
      uiRef.current = ui;

      ui.configure({
        controlPanelElements: [
          "play_pause",
          "time_and_duration",
          "spacer",
          "mute",
          "volume",
          "fullscreen",
          "overflow_menu",
        ],
        overflowMenuButtons: ["quality", "language", "playback_rate", "picture_in_picture"],
      });

      player.configure({
        abr: {
          enabled: true,
          restrictions: {
            maxHeight: 1080,
          },
        },
      });

      if (enableMiniPlayer) {
        registerCardVideoElement(video);
      }

      try {
        await player.load(src);
        if (cancelled) return;

        const seekAndPlay = () => {
          if (cancelled) return;
          if (resumeAtOnLoad > 0 && Number.isFinite(resumeAtOnLoad)) {
            try {
              video.currentTime = resumeAtOnLoad;
              currentTimeRef.current = resumeAtOnLoad;
            } catch {
              /* ignore seek errors before ready */
            }
          }
          if (shouldAutoPlay) {
            void video.play().catch(() => {
              // Autoplay may be blocked by the browser; controls remain available.
            });
          }
        };

        // Seek after media is ready so mini-player resume does not restart at 0.
        if (resumeAtOnLoad > 0 && video.readyState < 2) {
          video.addEventListener("loadeddata", seekAndPlay, { once: true });
        } else {
          seekAndPlay();
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load video:", error);
        }
      }
    };

    void initPlayer();

    return () => {
      cancelled = true;
      if (ui) {
        void ui.destroy();
      }
      if (player) {
        void player.destroy();
      }
      playerRef.current = null;
      uiRef.current = null;

      const video = videoRef.current;
      if (enableMiniPlayer && video) {
        releaseCardVideoElement(video);
      }
    };
  }, [src, enableMiniPlayer]);

  // Play tracking + mini-player handoff. Declared second so cleanup runs BEFORE
  // player destroy — while the video is still playing / readable.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => {
      wasPlayingRef.current = true;
      onPlayStateChange?.(true);
      if (enableMiniPlayer) {
        void playExclusiveCardMedia(video).catch(() => {});
      }
    };
    const onPause = () => {
      wasPlayingRef.current = false;
      onPlayStateChange?.(false);
    };
    const onEnded = () => {
      wasPlayingRef.current = false;
      onPlayStateChange?.(false);
    };
    const onTimeUpdate = () => {
      currentTimeRef.current = video.currentTime || 0;
    };

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);
    video.addEventListener("timeupdate", onTimeUpdate);

    return () => {
      const resumeAt = Math.max(video.currentTime || 0, currentTimeRef.current || 0);
      const stillPlaying = wasPlayingRef.current || (!video.paused && !video.ended);

      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("timeupdate", onTimeUpdate);

      if (enableMiniPlayer && src && stillPlaying && !handedOffRef.current) {
        handedOffRef.current = true;
        wasPlayingRef.current = false;
        promoteCardShakaToMiniPlayer(src, resumeAt, {
          ...metaRef.current,
          mediaUrl: src,
        });
      }
    };
  }, [enableMiniPlayer, onPlayStateChange, src]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    container.addEventListener("contextmenu", handleContextMenu, true);

    return () => {
      container.removeEventListener("contextmenu", handleContextMenu, true);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="shaka-player-container"
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      <video
        ref={videoRef}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
        playsInline
      />
    </div>
  );
};

export default ShakaPlayerVideo;
